import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";

export interface RioProgressEvent {
  event: string;
  tool?: string;
  [key: string]: unknown;
}

interface InboundWSMessage {
  service?: string;
  action?: string;
  payload?: {
    message?: string;
    status?: number;
    templateId?: string;
    [key: string]: unknown;
  };
}

interface UseRioProgressOptions {
  /** Stop connecting / disconnect when false. The drawer passes `open`. */
  enabled: boolean;
  /**
   * Channel id for the Rio progress topic — either a numeric template
   * id (template Rio) or a prompt key string (prompt Rio). Both surfaces
   * publish to the shared `rio-progress:{id}` topic on bun-websocket;
   * template ids and prompt keys don't collide because template ids are
   * integers and prompt keys are alphanumeric_snake_case.
   *
   * Callers should pass EITHER templateId OR promptKey, not both. The
   * hook coerces whichever is present to a string for the subscription.
   */
  templateId?: number;
  promptKey?: string;
}

/**
 * WebSocket subscription to Rio's per-channel progress topic.
 *
 * Lifecycle:
 *  - When `enabled` flips to true and a channel id (templateId or promptKey)
 *    is provided, opens a WS to `VITE_WEBSOCKET_URL`, then sends a
 *    `rio.subscribe` message with the user's Auth0 access token.
 *  - Reconnects automatically with exponential backoff (1s → 2s → 4s → 8s → 16s)
 *    on unexpected close. Stops reconnecting when `enabled` flips to false
 *    or the component unmounts.
 *  - `latestEvent` holds the most recent received progress event so the
 *    drawer can render dynamic status text. Cleared when `enabled` goes
 *    false.
 *
 * Auth: every message sent over the WS carries the user's access token
 * (bun-websocket re-validates on every message). The hook silently
 * refreshes the token via `getAccessTokenSilently` before sending.
 */
export function useRioProgress({ enabled, templateId, promptKey }: UseRioProgressOptions) {
  const { getAccessTokenSilently } = useAuth0();
  const [latestEvent, setLatestEvent] = useState<RioProgressEvent | null>(null);
  const [connectionState, setConnectionState] = useState<"idle" | "connecting" | "open" | "closed">("idle");

  // Ref pointing at the active socket so cleanup doesn't race with
  // reconnect attempts. Backoff state also lives in refs so re-renders
  // don't reset it.
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  // Resolve the channel id (templateId wins if both are passed, but the
  // drawer surfaces should only ever pass one). Empty string means
  // "not ready" — matches the previous "templateId <= 0" no-op check.
  const channelId =
    templateId !== undefined && Number.isFinite(templateId) && templateId > 0
      ? String(templateId)
      : promptKey && promptKey.length > 0
        ? promptKey
        : "";

  useEffect(() => {
    if (!enabled || !channelId) {
      return;
    }
    cancelledRef.current = false;
    const url = import.meta.env.VITE_WEBSOCKET_URL as string | undefined;
    if (!url) {
      // Misconfigured env — fail soft. Drawer falls back to static
      // "Thinking..." rendering when latestEvent stays null.
      console.warn("useRioProgress: VITE_WEBSOCKET_URL not set");
      return;
    }

    const connect = async () => {
      if (cancelledRef.current) return;
      setConnectionState("connecting");

      let accessToken: string;
      try {
        accessToken = await getAccessTokenSilently();
      } catch (err) {
        console.warn("useRioProgress: failed to get access token", err);
        scheduleReconnect();
        return;
      }
      if (cancelledRef.current) return;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelledRef.current) {
          ws.close();
          return;
        }
        setConnectionState("open");
        reconnectAttemptRef.current = 0;
        ws.send(
          JSON.stringify({
            service: "rio",
            action: "subscribe",
            accessToken,
            timestamp: new Date().toISOString(),
            // bun-websocket's rio topic scheme uses `rio-progress:{id}` where
            // {id} is a raw string. Template Rio passes stringified numeric
            // ids; prompt Rio passes prompt keys. The server just topic-
            // routes on the string.
            payload: { templateId: channelId },
          }),
        );
      };

      ws.onmessage = (msg) => {
        let parsed: InboundWSMessage;
        try {
          parsed = JSON.parse(msg.data as string) as InboundWSMessage;
        } catch {
          return;
        }
        // We only care about Rio progress broadcasts for this channel.
        // Ack messages (subscribe/unsubscribe) come back with action
        // "subscribe"/"unsubscribe" — skip those.
        if (parsed.service !== "rio" || parsed.action !== "progress" || !parsed.payload?.message) {
          return;
        }
        if (parsed.payload.templateId && parsed.payload.templateId !== channelId) {
          return;
        }
        try {
          const event = JSON.parse(parsed.payload.message) as RioProgressEvent;
          setLatestEvent(event);
        } catch {
          // ignore malformed inner payload
        }
      };

      ws.onerror = () => {
        // onclose will follow; defer reconnect to that handler so we
        // don't try twice.
      };

      ws.onclose = () => {
        setConnectionState("closed");
        wsRef.current = null;
        if (!cancelledRef.current) {
          scheduleReconnect();
        }
      };
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current) return;
      const attempt = Math.min(reconnectAttemptRef.current, 4);
      const delayMs = 1000 * 2 ** attempt;
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(() => {
        void connect();
      }, delayMs);
    };

    void connect();

    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setLatestEvent(null);
      setConnectionState("idle");
      reconnectAttemptRef.current = 0;
    };
  }, [enabled, channelId, getAccessTokenSilently]);

  return { latestEvent, connectionState };
}
